/**
 * Graphics Overlay Layer Component
 * Manages value-based graphics overlays on plant images
 */

import React, { useMemo } from 'react';
import {
  Box,
  Tooltip,
  Typography,
} from '@mui/material';

// Types
interface OverlayTrigger {
  id: string;
  tagId: number;
  condition: string; // e.g., "value > 150", "value === 1", "value !== null"
  logicOperator?: 'AND' | 'OR'; // For multiple conditions
}

interface GraphicsOverlay {
  id: number;
  name: string;
  description: string;
  overlay_type: 'image' | 'icon' | 'shape' | 'text' | 'badge' | 'gauge' | 'trend';
  site_name: string;
  plant_area_name: string;
  trigger_tag_name: string;
  trigger_tag_description: string;
  trigger_condition: string;
  trigger_value?: string;
  trigger_value_max?: string;
  current_trigger_value: any;
  width: number; // percentage
  height: number; // percentage
  z_index: number;
  visual_config: any; // JSON configuration
  overlay_image?: string; // URL to uploaded image
  image_scaling_mode: 'exact' | 'proportional' | 'cover' | 'contain';
  pixel_offset_x: number;
  pixel_offset_y: number;
  click_action: 'none' | 'graph' | 'table' | 'custom_url' | 'popup_info';
  target_table_badge?: number; // ID of target table badge
  custom_url?: string;
  click_config: any; // JSON configuration for click actions
  is_active: boolean;
  // Legacy support for existing overlay structure
  triggers?: OverlayTrigger[];
  graphics?: {
    type: 'svg' | 'image' | 'shape' | 'animation';
    content: string;
    width: number;
    height: number;
    animation?: {
      type: 'rotate' | 'pulse' | 'blink' | 'scale';
      duration: number;
      direction?: 'normal' | 'reverse' | 'alternate';
    };
  };
  position?: {
    x: number;
    y: number;
    unit: 'percent' | 'pixel';
  };
  tooltip?: string;
  isVisible?: boolean;
}

interface TagData {
  id: number;
  tag_name: string;
  current_value: number | boolean | string | null;
  alarm_status: string;
}

interface GraphicsOverlayLayerProps {
  overlays: GraphicsOverlay[];
  positionedElements?: Array<{
    id: number;
    content_type: string;
    object_id: number;
    position_x: number;
    position_y: number;
    z_index: number;
    is_visible: boolean;
  }>;
  tags: TagData[];
  imageWidth: number;
  imageHeight: number;
  containerWidth: number;
  containerHeight: number;
  onOverlayClick?: (overlay: GraphicsOverlay) => void;
}

// Overlay component for individual graphics
const OverlayGraphic: React.FC<{
  overlay: GraphicsOverlay;
  isActive: boolean;
  scaleX: number;
  scaleY: number;
  onClick?: () => void;
  position?: { x: number; y: number }; // Position from unified positioning system
}> = ({ overlay, isActive, scaleX, scaleY, onClick, position: unifiedPosition }) => {
  // Support both new backend structure and legacy structure
  const graphics = overlay.graphics || {
    type: overlay.overlay_type === 'image' ? 'image' as const : 
          overlay.overlay_type === 'shape' ? 'shape' as const : 'svg' as const,
    content: overlay.overlay_image || 
             overlay.visual_config?.content || 
             `${overlay.overlay_type}:${overlay.visual_config?.color || '#1976d2'}`,
    width: overlay.width,
    height: overlay.height,
    animation: overlay.visual_config?.animation,
  };
  
  const position = unifiedPosition ? {
    x: unifiedPosition.x,
    y: unifiedPosition.y,
    unit: 'percent' as const
  } : overlay.position || {
    x: 50, y: 50, unit: 'percent' as const // default center position
  };
  
  const tooltip = overlay.description || overlay.tooltip || overlay.name;

  // Calculate actual position based on scale with pixel-perfect offsets
  const baseX = position.unit === 'percent' 
    ? (position.x / 100) * 100
    : position.x;
  
  const baseY = position.unit === 'percent'
    ? (position.y / 100) * 100
    : position.y;
  
  // Add pixel offsets for precise alignment, scaled appropriately
  const pixelOffsetX = (overlay.pixel_offset_x || 0) * scaleX;
  const pixelOffsetY = (overlay.pixel_offset_y || 0) * scaleY;
  
  const actualX = baseX + (pixelOffsetX / window.innerWidth) * 100; // Convert pixel offset to percentage
  const actualY = baseY + (pixelOffsetY / window.innerHeight) * 100;

  // Calculate actual size based on scale
  const actualWidth = graphics.width * Math.min(scaleX, scaleY);
  const actualHeight = graphics.height * Math.min(scaleX, scaleY);

  // Generate animation styles
  const getAnimationStyles = () => {
    if (!graphics.animation || !isActive) return {};

    const { type, duration, direction = 'normal' } = graphics.animation;
    
    const animations: { [key: string]: string } = {
      rotate: `spin ${duration}ms linear infinite ${direction}`,
      pulse: `pulse ${duration}ms ease-in-out infinite ${direction}`,
      blink: `blink ${duration}ms linear infinite ${direction}`,
      scale: `scale ${duration}ms ease-in-out infinite ${direction}`,
    };

    return {
      animation: animations[type],
    };
  };

  // Render graphics content
  const renderGraphicsContent = () => {
    switch (graphics.type) {
      case 'svg':
        return (
          <div
            dangerouslySetInnerHTML={{ __html: graphics.content }}
            style={{
              width: actualWidth,
              height: actualHeight,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          />
        );

      case 'image':
        // Calculate image dimensions based on scaling mode
        const getImageStyles = () => {
          const baseStyles = {
            maxWidth: '100%',
            maxHeight: '100%',
          };

          switch (overlay.image_scaling_mode || 'proportional') {
            case 'exact':
              return {
                ...baseStyles,
                width: 'auto',
                height: 'auto',
                objectFit: 'none' as const,
              };
            case 'proportional':
              return {
                ...baseStyles,
                width: actualWidth,
                height: actualHeight,
                objectFit: 'contain' as const,
              };
            case 'cover':
              return {
                ...baseStyles,
                width: actualWidth,
                height: actualHeight,
                objectFit: 'cover' as const,
              };
            case 'contain':
              return {
                ...baseStyles,
                width: actualWidth,
                height: actualHeight,
                objectFit: 'contain' as const,
              };
            default:
              return {
                ...baseStyles,
                width: actualWidth,
                height: actualHeight,
                objectFit: 'contain' as const,
              };
          }
        };

        return (
          <img
            src={graphics.content}
            alt={overlay.name}
            style={getImageStyles()}
            onError={(e) => {
              console.error('Failed to load overlay image:', graphics.content);
              e.currentTarget.style.display = 'none';
            }}
          />
        );

      case 'shape':
        // Parse shape definition (e.g., "circle:red", "rectangle:blue:dashed")
        const [shape, color = '#1976d2', strokeStyle = 'solid'] = graphics.content.split(':');
        
        if (shape === 'circle') {
          return (
            <div
              style={{
                width: actualWidth,
                height: actualHeight,
                borderRadius: '50%',
                backgroundColor: color,
                border: strokeStyle === 'dashed' ? `2px dashed ${color}` : `2px solid ${color}`,
              }}
            />
          );
        } else if (shape === 'rectangle') {
          return (
            <div
              style={{
                width: actualWidth,
                height: actualHeight,
                backgroundColor: color,
                border: strokeStyle === 'dashed' ? `2px dashed ${color}` : `2px solid ${color}`,
              }}
            />
          );
        }
        return null;

      case 'animation':
        // For animated GIFs or other animated content
        return (
          <img
            src={graphics.content}
            alt={overlay.name}
            style={{
              width: actualWidth,
              height: actualHeight,
              objectFit: 'contain',
            }}
          />
        );

      default:
        return null;
    }
  };

  const overlayElement = (
    <Box
      sx={{
        position: 'absolute',
        left: `${actualX}%`,
        top: `${actualY}%`,
        transform: 'translate(-50%, -50%)',
        zIndex: overlay.z_index || 10,
        cursor: onClick ? 'pointer' : 'default',
        opacity: isActive ? 1 : 0.3,
        transition: 'opacity 0.3s ease-in-out',
        pointerEvents: isActive ? 'auto' : 'none',
        ...getAnimationStyles(),
      }}
      onClick={onClick}
    >
      {renderGraphicsContent()}
    </Box>
  );

  // Add keyframe animations to the document if they don't exist
  React.useEffect(() => {
    const style = document.getElementById('overlay-animations');
    if (!style) {
      const styleSheet = document.createElement('style');
      styleSheet.id = 'overlay-animations';
      styleSheet.textContent = `
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.1); }
        }
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
        @keyframes scale {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.2); }
        }
      `;
      document.head.appendChild(styleSheet);
    }
  }, []);

  if (tooltip) {
    return (
      <Tooltip title={tooltip} arrow placement="top">
        {overlayElement}
      </Tooltip>
    );
  }

  return overlayElement;
};

// Function to evaluate trigger conditions
const evaluateCondition = (condition: string, value: any): boolean => {
  try {
    // Replace 'value' in the condition string with the actual value
    const sanitizedCondition = condition
      .replace(/value/g, JSON.stringify(value))
      .replace(/[^0-9\s+\-*/.()><=!&|"']/g, ''); // Basic sanitization

    // Use Function constructor for safe evaluation (safer than eval)
    return new Function('return ' + sanitizedCondition)();
  } catch (error) {
    console.warn(`Failed to evaluate condition "${condition}":`, error);
    return false;
  }
};

// Function to check if overlay should be active
const isOverlayActive = (overlay: GraphicsOverlay, tags: TagData[]): boolean => {
  if (!overlay.is_active) return false;
  
  // Legacy support
  if (overlay.isVisible !== undefined && !overlay.isVisible) return false;
  if (overlay.triggers && overlay.triggers.length > 0) {
    const results = overlay.triggers.map(trigger => {
      const tag = tags.find(t => t.id === trigger.tagId);
      if (!tag) return false;
      return evaluateCondition(trigger.condition, tag.current_value);
    });
    
    const operator = overlay.triggers[0]?.logicOperator || 'AND';
    if (operator === 'OR') {
      return results.some(result => result);
    } else {
      return results.every(result => result);
    }
  }

  // New backend structure
  if (overlay.trigger_condition === 'always') return true;
  
  const triggerTag = tags.find(t => t.tag_name === overlay.trigger_tag_name);
  if (!triggerTag) return false;
  
  const value = triggerTag.current_value;
  
  switch (overlay.trigger_condition) {
    case 'value_equals':
      return String(value) === overlay.trigger_value;
    case 'value_greater':
      return Number(value) > Number(overlay.trigger_value || 0);
    case 'value_less':
      return Number(value) < Number(overlay.trigger_value || 0);
    case 'value_between':
      const min = Number(overlay.trigger_value || 0);
      const max = Number(overlay.trigger_value_max || 100);
      return Number(value) >= min && Number(value) <= max;
    case 'boolean_true':
      return Boolean(value) === true;
    case 'boolean_false':
      return Boolean(value) === false;
    case 'alarm_active':
      return triggerTag.alarm_status !== 'NORMAL';
    case 'quality_bad':
      return triggerTag.alarm_status === 'BAD_QUALITY';
    default:
      return false;
  }
};

export const GraphicsOverlayLayer: React.FC<GraphicsOverlayLayerProps> = ({
  overlays,
  positionedElements = [],
  tags,
  imageWidth,
  imageHeight,
  containerWidth,
  containerHeight,
  onOverlayClick,
}) => {
  // Calculate scale factors
  const scaleX = containerWidth / imageWidth;
  const scaleY = containerHeight / imageHeight;

  // Create a map of positioned elements by object_id for graphics overlays
  const overlayPositions = useMemo(() => {
    const positionMap = new Map();
    positionedElements.forEach(pos => {
      if (pos.content_type === 'sites.graphicsoverlayconfig') {
        positionMap.set(pos.object_id, {
          x: pos.position_x,
          y: pos.position_y,
          z_index: pos.z_index,
          is_visible: pos.is_visible
        });
      }
    });
    return positionMap;
  }, [positionedElements]);

  // Determine which overlays should be active
  const activeOverlays = useMemo(() => {
    return overlays.map(overlay => {
      const position = overlayPositions.get(overlay.id);
      return {
        overlay,
        isActive: isOverlayActive(overlay, tags),
        position: position && position.is_visible ? { x: position.x, y: position.y } : null,
        zIndex: position?.z_index || overlay.z_index || 1
      };
    }).filter(({ position, isActive }) => isActive && position); // Only show positioned and active overlays
  }, [overlays, tags, overlayPositions]);

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none', // Allow clicks to pass through to the underlying image
      }}
    >
      {activeOverlays.map(({ overlay, isActive, position, zIndex }) => (
        <OverlayGraphic
          key={overlay.id}
          overlay={{ ...overlay, z_index: zIndex }}
          isActive={isActive}
          scaleX={scaleX}
          scaleY={scaleY}
          position={position || undefined}
          onClick={() => onOverlayClick?.(overlay)}
        />
      ))}
    </Box>
  );
};

// Utility functions for creating common overlay types

// Helper function to create base overlay structure
const createBaseOverlay = (
  id: number,
  name: string,
  description: string = '',
  overlayType: 'icon' | 'shape' | 'text' | 'badge' | 'gauge' | 'trend' = 'icon'
): Partial<GraphicsOverlay> => ({
  id,
  name,
  description,
  overlay_type: overlayType,
  site_name: '',
  plant_area_name: '',
  trigger_tag_name: '',
  trigger_tag_description: '',
  trigger_condition: 'always',
  current_trigger_value: null,
  width: 40,
  height: 40,
  z_index: 10,
  visual_config: {},
  click_action: 'none',
  click_config: {},
  is_active: true,
});

export const createPumpOverlay = (
  id: number,
  name: string,
  runningTagId: number,
  position: { x: number; y: number },
  size: { width: number; height: number } = { width: 40, height: 40 }
): GraphicsOverlay => ({
  id,
  name,
  triggers: [
    {
      id: `${id}_running`,
      tagId: runningTagId,
      condition: 'value === true || value === 1',
    },
  ],
  graphics: {
    type: 'svg',
    content: `
      <svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
        <circle cx="20" cy="20" r="18" fill="#4caf50" stroke="#2e7d32" stroke-width="2"/>
        <path d="M15 12 L15 28 L28 20 Z" fill="white"/>
      </svg>
    `,
    width: size.width,
    height: size.height,
    animation: {
      type: 'pulse',
      duration: 1000,
    },
  },
  position: {
    x: position.x,
    y: position.y,
    unit: 'percent',
  },
  z_index: 15,
  tooltip: `${name} - Running`,
  isVisible: true,
} as GraphicsOverlay);

export const createValveOverlay = (
  id: number,
  name: string,
  openTagId: number,
  closedTagId: number,
  position: { x: number; y: number },
  size: { width: number; height: number } = { width: 30, height: 30 }
): GraphicsOverlay[] => [
  // Open valve overlay
  {
    id: id * 10 + 1, // Generate unique numeric ID
    name: `${name} Open`,
    triggers: [
      {
        id: `${id}_open_trigger`,
        tagId: openTagId,
        condition: 'value === true || value === 1',
      },
    ],
    graphics: {
      type: 'svg',
      content: `
        <svg viewBox="0 0 30 30" xmlns="http://www.w3.org/2000/svg">
          <rect x="5" y="13" width="20" height="4" fill="#4caf50" stroke="#2e7d32" stroke-width="1"/>
          <circle cx="15" cy="15" r="8" fill="none" stroke="#4caf50" stroke-width="2"/>
          <text x="15" y="18" text-anchor="middle" fill="#4caf50" font-size="8">O</text>
        </svg>
      `,
      width: size.width,
      height: size.height,
    },
    position: {
      x: position.x,
      y: position.y,
      unit: 'percent',
    },
    z_index: 15,
    tooltip: `${name} - Open`,
    isVisible: true,
  } as GraphicsOverlay,
  // Closed valve overlay
  {
    id: id * 10 + 2, // Generate unique numeric ID
    name: `${name} Closed`,
    triggers: [
      {
        id: `${id}_closed_trigger`,
        tagId: closedTagId,
        condition: 'value === true || value === 1',
      },
    ],
    graphics: {
      type: 'svg',
      content: `
        <svg viewBox="0 0 30 30" xmlns="http://www.w3.org/2000/svg">
          <rect x="5" y="13" width="20" height="4" fill="#f44336" stroke="#c62828" stroke-width="1"/>
          <circle cx="15" cy="15" r="8" fill="none" stroke="#f44336" stroke-width="2"/>
          <text x="15" y="18" text-anchor="middle" fill="#f44336" font-size="8">C</text>
        </svg>
      `,
      width: size.width,
      height: size.height,
    },
    position: {
      x: position.x,
      y: position.y,
      unit: 'percent',
    },
    z_index: 15,
    tooltip: `${name} - Closed`,
    isVisible: true,
  } as GraphicsOverlay,
];

export const createAlarmOverlay = (
  id: number,
  name: string,
  alarmTagId: number,
  position: { x: number; y: number },
  size: { width: number; height: number } = { width: 25, height: 25 }
): GraphicsOverlay => ({
  id,
  name,
  triggers: [
    {
      id: `${id}_alarm`,
      tagId: alarmTagId,
      condition: 'value === true || value === 1',
    },
  ],
  graphics: {
    type: 'svg',
    content: `
      <svg viewBox="0 0 25 25" xmlns="http://www.w3.org/2000/svg">
        <polygon points="12.5,2 22,20 3,20" fill="#f44336" stroke="#c62828" stroke-width="1"/>
        <text x="12.5" y="16" text-anchor="middle" fill="white" font-size="10" font-weight="bold">!</text>
      </svg>
    `,
    width: size.width,
    height: size.height,
    animation: {
      type: 'blink',
      duration: 1000,
    },
  },
  position: {
    x: position.x,
    y: position.y,
    unit: 'percent',
  },
  z_index: 20, // Higher z-index for alarms
  tooltip: `${name} - ALARM`,
  isVisible: true,
} as GraphicsOverlay);

export default GraphicsOverlayLayer;

